import { z } from "zod"

const ACCOUNTS_KEY = "accounts"
export const ACTIVE_ACCOUNT_KEY = "activeAccountEmail"

const StoredAccountSchema = z.object({
  email: z.string(),
  name: z.string(),
  idToken: z.string(),
  refreshToken: z.string(),
  storeId: z.string().nullable(),
  lastUsedAt: z.string(),
})

export type StoredAccount = z.infer<typeof StoredAccountSchema>

interface AccountStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

/**
 * Every account logged in on this device. The active one also lives on the
 * legacy `idToken`/`refreshToken`/`storeId` keys that the rest of the app
 * reads; the others are revived from their refresh tokens when switched to.
 *
 * Storage failures are swallowed like in the token source: a browser that
 * refuses storage simply behaves as if only the active account exists.
 */
export const createAccountRegistry = (storage: AccountStorage) => {
  const readAll = (): StoredAccount[] => {
    try {
      const raw = storage.getItem(ACCOUNTS_KEY)
      if (!raw) return []
      const parsed = z.array(StoredAccountSchema).safeParse(JSON.parse(raw))
      return parsed.success ? parsed.data : []
    } catch (error) {
      console.error("Failed to read accounts:", error)
      return []
    }
  }

  const writeAll = (accounts: StoredAccount[]) => {
    try {
      storage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts))
    } catch (error) {
      console.error("Failed to store accounts:", error)
    }
  }

  /** Most recently used first. */
  const list = (): StoredAccount[] =>
    readAll().sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt))

  const find = (email: string): StoredAccount | null =>
    readAll().find((account) => account.email === email) ?? null

  const upsert = (account: Omit<StoredAccount, "lastUsedAt">) => {
    const others = readAll().filter(({ email }) => email !== account.email)
    writeAll([...others, { ...account, lastUsedAt: new Date().toISOString() }])
  }

  const remove = (email: string) => {
    writeAll(readAll().filter((account) => account.email !== email))
  }

  const getActiveEmail = (): string | null => {
    try {
      return storage.getItem(ACTIVE_ACCOUNT_KEY)
    } catch {
      return null
    }
  }

  const setActiveEmail = (email: string | null) => {
    try {
      if (email) storage.setItem(ACTIVE_ACCOUNT_KEY, email)
      else storage.removeItem(ACTIVE_ACCOUNT_KEY)
    } catch (error) {
      console.error("Failed to store active account:", error)
    }
  }

  return { list, find, upsert, remove, getActiveEmail, setActiveEmail }
}

export type AccountRegistry = ReturnType<typeof createAccountRegistry>
